import { isAxiosError } from 'axios';

import { useAxios } from '@/hooks/useAxios';
import { type CostStatus } from '@/hooks/useConversationCost';
import { getQueryKey, useGetUserKey } from '@/hooks/useQueryKeys';
import { useQuery } from '@/utils/api';

// `CostStatus` is owned by `useConversationCost`; re-export it so hunt-cost
// consumers can import the shared shape from this hook too.
export type { CostStatus };

/**
 * Reads the on-demand USD cost for a single Hunt. Praetorian-only
 * (`EntitlementPraetorian`) — callers MUST gate `enabled` on
 * `userType === 'praetorian'` so the query never fires for a
 * non-Praetorian viewer. `enabled` defaults to `false` (safe-by-default)
 * so a forgotten gate fails closed (no query) rather than open. Returns
 * `null` on 404 (unowned/unknown hunt); a `200` with `cost: 0` renders as
 * `$0.00`, not hidden.
 */
export const useHuntCost = (
  huntUuid?: string,
  opts?: { enabled?: boolean }
) => {
  const axios = useAxios();

  const queryKey = useGetUserKey(getQueryKey.getHuntCost(huntUuid ?? ''));

  return useQuery<CostStatus | null>({
    queryKey,
    enabled: (opts?.enabled ?? false) && !!huntUuid,
    queryFn: async () => {
      try {
        const { data } = await axios.get<CostStatus>(
          `/hunt/${encodeURIComponent(huntUuid ?? '')}/cost`
        );
        return data;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    retry: false,
    staleTime: 30_000,
    gcTime: 300_000,
    defaultErrorMessage: 'Failed to load hunt cost',
  });
};
